import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Lock, Mail, ShieldCheck, ArrowLeft, AlertCircle } from 'lucide-react'
import { Button, Input, cn } from '../atoms'
import { useAuth, TwoFactorChallenge } from '../../store/auth.store'
import { useErrorToast, useSuccessToast } from '../atoms/Toaster'

function ComCenterLogo({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 220" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Envelope body */}
      <rect x="55" y="70" width="90" height="65" rx="4" fill="#0D2E4D"/>
      {/* Envelope flap */}
      <path d="M55 74 L100 110 L145 74" fill="none" stroke="#0D2E4D" strokeWidth="3"/>
      <path d="M55 135 L85 105" fill="none" stroke="#0D2E4D" strokeWidth="2.5"/>
      <path d="M145 135 L115 105" fill="none" stroke="#0D2E4D" strokeWidth="2.5"/>
      {/* Letter C - outer */}
      <path d="M100 20 C55 20 20 55 20 100 C20 145 55 170 90 170"
        fill="none" stroke="#1A9E8F" strokeWidth="18" strokeLinecap="round"/>
      {/* Speed lines */}
      <line x1="8" y1="75" x2="30" y2="75" stroke="#1A9E8F" strokeWidth="5" strokeLinecap="round"/>
      <line x1="2" y1="95" x2="28" y2="95" stroke="#1A9E8F" strokeWidth="5" strokeLinecap="round"/>
      <line x1="8" y1="115" x2="30" y2="115" stroke="#1A9E8F" strokeWidth="5" strokeLinecap="round"/>
      {/* Text ComCenter */}
      <text x="100" y="200" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="22" fill="#1A9E8F">ComCenter</text>
      <text x="100" y="218" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="9" fill="#6B7280">Correos. Casos. Conectados.</text>
    </svg>
  )
}

type Stage = 'credentials' | 'twoFactor'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stage, setStage] = useState<Stage>('credentials')
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null)
  const [code, setCode] = useState('')
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('expired') === '1'
  const loopDetected = searchParams.get('loop') === '1'

  const { login, verifyTwoFactor } = useAuth()
  const navigate = useNavigate()
  const showError = useErrorToast()
  const showSuccess = useSuccessToast()

  useEffect(() => {
    if (sessionExpired) {
      showError('Sesión expirada', 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.')
    }
    if (loopDetected) {
      showError(
        'Problema detectado',
        'Tu navegador tenía credenciales corruptas. Se ha limpiado el estado. Intenta iniciar sesión.',
      )
      // Limpiar TODO el storage residual
      localStorage.clear()
      sessionStorage.clear()
    }
  }, [sessionExpired, loopDetected, showError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const result = await login(email, password)
      if (result.twoFactorRequired && result.challenge) {
        setChallenge(result.challenge)
        setStage('twoFactor')
      } else {
        showSuccess('¡Bienvenido!', 'Has iniciado sesión correctamente')
        navigate('/')
      }
    } catch (error: any) {
      showError('Error de autenticación', error.message || 'Credenciales inválidas')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!challenge) return
    setIsSubmitting(true)
    try {
      const result = await verifyTwoFactor(challenge.tempToken, code.replace(/\s/g, ''))
      showSuccess('¡Bienvenido!', 'Has iniciado sesión correctamente')
      navigate('/')
    } catch (error: any) {
      showError('Código inválido', error.message || 'Verifica el código e intenta de nuevo')
      setCode('')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    setStage('credentials')
    setChallenge(null)
    setCode('')
  }

  if (stage === 'twoFactor' && challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                <ShieldCheck className="h-8 w-8 text-primary-600" />
              </div>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">
              Verificación en 2 pasos
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Hola, <span className="font-medium">{challenge.user.firstName}</span>. Ingresa el código de 6 dígitos de tu app autenticadora.
            </p>
            <p className="mt-1 text-xs text-gray-400">{challenge.user.email}</p>
          </div>

          <form className="mt-8 space-y-6 card p-6" onSubmit={handleVerify2FA}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2 text-center">
                Código de verificación
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={9}
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={isSubmitting}
                autoFocus
                className="block w-full text-center text-2xl font-mono tracking-[0.5em] px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                También puedes usar un código de respaldo (XXXX-XXXX)
              </p>
            </div>

            <Button type="submit" className="w-full" isLoading={isSubmitting} disabled={code.length < 4}>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verificar
            </Button>

            <button
              type="button"
              onClick={handleBack}
              className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <ComCenterLogo className="w-36 h-40" />
          </div>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Iniciar Sesión
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Gestión de comunicaciones - IA
          </p>
        </div>

        {sessionExpired && (
          <div className={cn(
            'flex items-start gap-2 p-3 rounded-lg border',
            'bg-amber-50 border-amber-200 text-amber-800'
          )}>
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-semibold">Sesión expirada</p>
              <p>Por seguridad, vuelve a iniciar sesión para continuar.</p>
            </div>
          </div>
        )}

        {loopDetected && (
          <div className={cn(
            'flex items-start gap-2 p-3 rounded-lg border',
            'bg-red-50 border-red-200 text-red-800'
          )}>
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-semibold">Estado limpiado</p>
              <p>Tu navegador tenía credenciales corruptas. Inicia sesión para continuar.</p>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6 card p-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              label="Correo electrónico"
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              leftIcon={<Mail className="w-4 h-4 text-gray-400" />}
            />

            <Input
              label="Contraseña"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              leftIcon={<Lock className="w-4 h-4 text-gray-400" />}
            />
          </div>

          <div className="flex items-center justify-end text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <Button type="submit" className="w-full" isLoading={isSubmitting}>
            Iniciar sesión
          </Button>
        </form>

        <div className="text-center text-xs text-gray-400">
          <p>© 2026 ComCenter - Todos los derechos reservados</p>
          <p className="mt-1">Correos. Casos. Conectados.</p>
        </div>
      </div>
    </div>
  )
}
